import { useState } from "react"
import { DateTime } from "luxon"
import {
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardNoResultText } from "../../../../../shared/components/noResult/StandardNoResultText"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { BalanceDetailsDialog } from "../components/balanceDetails/BalanceDetailsDialog"
import { SelectMerchantAutocomplete } from "../components/SelectMerchantAutocomplete"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useExportBalanceToCsv } from "../hooks/useExportBalanceToCsv"
import { SUPPLIER_ADMIN_ROUTE } from "../../../../../constants/routes"
import { AcquiringReportType } from "../supplierAcquiringReportsTypes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useSupplier } from "../../../supplierHooks"
import {
    SupplierAcquiringReportsQuery,
    SupplierBalanceResponse,
    fetchSupplierBalance,
} from "../supplierAcquiringReportsApi"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()((theme) => ({
    row: {
        "& > th": {
            border: "none",
            color: theme.palette.tailwind.slate[700],
        },
    },
    totalRow: {
        borderTop: "1px solid #CACACA",
    },
    balanceRow: {
        borderTop: "1px solid #585858",
        "& > th": {
            color: theme.palette.text.primary,
        },
    },
    dateRangeFilter: {
        "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.common.white,
        },
    },
}))

export type DataStateType = {
    startDate: DateTime
    endDate: DateTime
}

const selectBalance = (data: SupplierBalanceResponse) => data?.acquiringReports || []

export function Balance() {
    const { classes, cx } = useStyles()
    const { supplierId } = useSupplier()
    const moneyFactory = useMoney({})

    const maxDate = DateTime.now().minus({ days: 1 })
    const { startDate, endDate } = useInitialDateFilters(maxDate, maxDate)
    const [dateState, setDateState] = useState<DataStateType>({
        startDate: startDate,
        endDate: endDate,
    })

    const [merchantId, setMerchantId] = useState<string>()
    const [showDetailsDialog, setShowDetailsDialog] = useState(false)

    const {
        data: balanceData,
        isLoading,
        isFetching,
    } = useApiQuery(fetchSupplierBalance, {
        queryName: SupplierAcquiringReportsQuery.BALANCE_LIST,
        dependencies: {
            supplierId,
            fromDate: dateState.startDate.toISODate(),
            toDate: dateState.endDate.toISODate(),
            merchantId,
        },
        select: selectBalance,
    })

    const { handleExportRows } = useExportBalanceToCsv(
        dateState.startDate?.toISODate() ?? "",
        dateState.endDate?.toISODate() ?? "",
    )

    return (
        <>
            <LocalizedStrict id="supplier-overview-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPLIER_ADMIN_BALANCE} title="Balance">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-acquiring-reports">
                            <Breadcrumb>Reconciliation</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-balance">
                            <Breadcrumb>Balance</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container wrap="nowrap" alignItems="center" width="100%" paddingBottom="2rem">
                <Grid item container gap="1rem">
                    <StandardDateRangeFilter
                        dateFilterClasses={classes.dateRangeFilter}
                        value={dateState}
                        onChange={setDateState}
                    />
                    <SelectMerchantAutocomplete
                        merchantId={merchantId}
                        setMerchantId={(merchantId) => setMerchantId(merchantId)}
                    />
                </Grid>
                <Grid item alignItems="end">
                    <StandardExportButton
                        onClick={() => {
                            handleExportRows(balanceData)
                        }}
                        bordered
                    />
                </Grid>
            </Grid>
            {isLoading && <LoadingSpinner />}
            {!balanceData && !isFetching && <StandardNoResultText />}

            {balanceData && (
                <>
                    <TableContainer component={Paper} sx={{ marginBottom: "2rem" }}>
                        <Table sx={{ minWidth: 500 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#F8F8F8", textAlign: "center" }}>
                                    <TableCell sx={{ width: "auto" }}> </TableCell>

                                    <LocalizedStrict id="supplier-overview-table-amount">
                                        <TableCell sx={{ width: "auto", textAlign: "end" }}> Amount </TableCell>
                                    </LocalizedStrict>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {balanceData.map((item, index) => {
                                    if (item.type === AcquiringReportType.BALANCE) {
                                        return (
                                            <TableRow key={item.name + index} className={classes.balanceRow}>
                                                <TableCell component="th" scope="row" sx={{ width: "250px" }}>
                                                    <Typography variant="body1" fontWeight={700}>
                                                        {item.name}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{ width: "100px", textAlign: "end" }}
                                                >
                                                    <Typography variant="body1" fontWeight={800}>
                                                        {moneyFactory.format(toDinero(item.amount))}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                    return (
                                        <>
                                            <TableRow
                                                key={item.name + index}
                                                className={cx(
                                                    classes.row,
                                                    item.type === AcquiringReportType.TOTAL && classes.totalRow,
                                                )}
                                            >
                                                <TableCell component="th" scope="row" sx={{ width: "250px" }}>
                                                    {item.name}
                                                </TableCell>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{ width: "100px", textAlign: "end" }}
                                                >
                                                    {moneyFactory.format(toDinero(item.amount))}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Grid container justifyContent="flex-end">
                        <LocalizedStrict id="supplier-overview-table-view-details">
                            <StandardButton fullWidth={false} onClick={() => setShowDetailsDialog(true)}>
                                View details
                            </StandardButton>
                        </LocalizedStrict>
                    </Grid>
                </>
            )}
            {balanceData && showDetailsDialog && (
                <BalanceDetailsDialog
                    open={showDetailsDialog}
                    onClose={() => setShowDetailsDialog(false)}
                    dateState={dateState}
                />
            )}
        </>
    )
}
